import { Col, Row } from "antd";
import { useContext, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../context/auth/context";
import AppContext from "../../../context/app/context";
import { getProductStatus } from "../../../models/product/enum";
import { getReportType, ReportType } from "../../../models/report/enum";
import EndPoints from "../../../services/end-points";
import { Permissions } from "../../../utils/Permissions/Permissions";
import FieldBuilder from "../../form-components/field-builder";

const Form = () => {
  const { t } = useTranslation();

  const [deliveryWorkers, setDeliveryWorkers] = useState<any[]>();
  const [deliveryWorkersLoading, setDeliveryWorkersLoading] = useState(true);

  const [stores, setStores] = useState<any[]>();
  const [storesLoading, setStoresLoading] = useState(true);

  const [trademarks, setTrademarks] = useState<any[]>();
  const [trademarksLoading, setTrademarksLoading] = useState(true);

  const { direction } = useContext(AppContext);
  const { userPermissions: permissions } = useContext(AuthContext);

  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];

  const canExportProductMarginReport =
    userPermissions.includes(Permissions.CAN_EXPORT_PRODUCT_MARGIN_REPORT) &&
    userPermissions.includes(Permissions.CAN_MANAGE_PURCHASE_PRICE);

  const type = [
    ReportType.PHARMACIES_ORDERS,
    ReportType.ORDER_USER_DETAILS,
    ReportType.ORDERS_CANCELLATION,
    ReportType.DRIVERS_PERFORMANCE,
    ReportType.ORDERS_DAYLE_INFO,
    ReportType.ORDERS_STATUS,
    ReportType.PAID_ORDERS_JOURNEY,
    ReportType.ORDERS_DETAILS,
    ReportType.DRIVER_PAYMENTS,
    ReportType.USER_ACTIVITY_REPORT,
    ReportType.MONTHLY_USER_ACTIVITY_REPORT,
    ReportType.LOW_STOCK_REPORT,
    ...(canExportProductMarginReport
      ? [ReportType.PRODUCT_MARGIN_REPORT]
      : []),
  ];

  const { control } = useFormContext();

  const typeWatch = useWatch({
    control,
    name: "type",
  });

  const startDateWatch = useWatch({
    control,
    name: "startDate",
  });

  const isLowStock = typeWatch === ReportType.LOW_STOCK_REPORT;
  const isProductMargin = typeWatch === ReportType.PRODUCT_MARGIN_REPORT;

  useEffect(() => {
    const getDeliveryWorker = async () => {
      try {
        const { data } = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
          light: true,
          perPage: -1,
        });
        setDeliveryWorkers(data?.data);
      } catch (error) {
      } finally {
        setDeliveryWorkersLoading(false);
      }
    };
    getDeliveryWorker();
  }, [direction]);

  useEffect(() => {
    const getStores = async () => {
      try {
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: [
            { name: "isNamlieah", value: 1, operation: "eq" },
          ],
        });
        setStores(data?.data);
      } catch (error) {
      } finally {
        setStoresLoading(false);
      }
    };
    getStores();
  }, [direction]);

  useEffect(() => {
    const getTrademarks = async () => {
      try {
        const { data } = await EndPoints.trademark.getAllTrademark({
          light: true,
          perPage: -1,
        });
        setTrademarks(data?.data);
      } catch (error) {
      } finally {
        setTrademarksLoading(false);
      }
    };
    getTrademarks();
  }, [direction]);

  return (
    <Row gutter={[16, 8]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
        <FieldBuilder
          name="type"
          label={`${t("type")}`}
          width="large"
          rules={{ required: true }}
          input={{
            type: "select",
            options: type.map((el) => {
              return {
                label: t(getReportType(el) as string),
                value: el,
              };
            }),
          }}
        />
      </Col>

      {isLowStock ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              rules={{ required: true }}
              input={{
                type: "select",
                loading: storesLoading,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="threshold"
              label={`${t("threshold")}`}
              width="large"
              rules={{ required: true, min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{}}
              input={{ type: "date-picker", format: "YYYY-MM-DD" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{}}
              input={{
                type: "date-picker",
                format: "YYYY-MM-DD",
                disabled: !startDateWatch,
              }}
            />
          </Col>
        </>
      ) : isProductMargin ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              input={{
                type: "select",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="trademark_id"
              label={`${t("trademark")}`}
              width="large"
              input={{
                type: "select",
                loading: trademarksLoading,
                allowSearch: true,
                options: trademarks?.map((trademark, index) => {
                  return {
                    key: index,
                    label: trademark?.name,
                    value: trademark?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="product_status"
              label={`${t("status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_quantity_status"
              label={`${t("store_status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
        </>
      ) : (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true }}
              input={{ type: "date-picker", format: "YYYY-MM-DD" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{ required: true }}
              input={{ type: "date-picker", format: "YYYY-MM-DD" }}
            />
          </Col>
        </>
      )}

      {typeWatch === ReportType.DRIVER_PAYMENTS ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="driverId"
            label={`${t("delivery_worker")}`}
            width="large"
            rules={{ required: true }}
            input={{
              type: "select",
              loading: deliveryWorkersLoading,
              options: deliveryWorkers?.map((user, index) => {
                return {
                  key: index,
                  label: user?.firstName + " " + user?.lastName,
                  value: user?.id,
                };
              }),
            }}
          />
        </Col>
      ) : typeWatch === ReportType.USER_ACTIVITY_REPORT ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="min_orders"
              label={`${t("min_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="max_orders"
              label={`${t("max_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
        </>
      ) : typeWatch === ReportType.MONTHLY_USER_ACTIVITY_REPORT ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="min_orders_per_month"
            label={`${t("min_orders_per_month")}`}
            width="large"
            rules={{ required: true, min: 1 }}
            input={{ type: "number" }}
          />
        </Col>
      ) : null}
    </Row>
  );
};

const ReportForm = () => {
  return <Form />;
};

export default ReportForm;
